/**
 * サービス全体で共有する状態語彙。
 *
 * DB の pgEnum・API のレスポンス・SPA のバッジ表示はすべてここを唯一の出典とする。
 * 日本語ラベルは `labels.ja.ts` に分離してあるので、値そのものは英語のまま扱うこと。
 */
import { z } from 'zod';

/* ------------------------------------------------------------ 契約・課金 */

export const ORGANIZATION_STATUS = ['pending', 'active', 'suspended', 'closing', 'closed'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUS)[number];
export const organizationStatusSchema = z.enum(ORGANIZATION_STATUS);

export const SUBSCRIPTION_STATUS = [
  'trialing',
  'active',
  'past_due',
  'suspended',
  'canceled',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUS);

export const INVOICE_STATUS = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[number];
export const invoiceStatusSchema = z.enum(INVOICE_STATUS);

/* ---------------------------------------------------------------- RBAC */

/** 運営者（staff_users）の権限。 */
export const STAFF_ROLE = ['superadmin', 'operator', 'support', 'auditor'] as const;
export type StaffRole = (typeof STAFF_ROLE)[number];
export const staffRoleSchema = z.enum(STAFF_ROLE);

/** 契約者（org_users）の権限。 */
export const ORG_ROLE = ['org_owner', 'org_admin', 'org_member'] as const;
export type OrgRole = (typeof ORG_ROLE)[number];
export const orgRoleSchema = z.enum(ORG_ROLE);

export const ACTOR_TYPE = ['staff', 'org_user', 'agent', 'system', 'api_key'] as const;
export type ActorType = (typeof ACTOR_TYPE)[number];
export const actorTypeSchema = z.enum(ACTOR_TYPE);

/* ------------------------------------------------------------ ノード関連 */

export const NODE_STATUS = [
  'provisioning',
  'enrolling',
  'active',
  'draining',
  'maintenance',
  'decommissioned',
] as const;
export type NodeStatus = (typeof NODE_STATUS)[number];
export const nodeStatusSchema = z.enum(NODE_STATUS);

export const NODE_HEALTH = ['healthy', 'degraded', 'critical', 'unknown'] as const;
export type NodeHealth = (typeof NODE_HEALTH)[number];
export const nodeHealthSchema = z.enum(NODE_HEALTH);

/**
 * ノードの役割（ARCHITECTURE.md §3.4）。
 * IMAP は MX を持つノードが必ず兼ねるため独立した役割にしない。
 */
export const NODE_ROLE = ['mx', 'mx+submission', 'submission-only'] as const;
export type NodeRole = (typeof NODE_ROLE)[number];
export const nodeRoleSchema = z.enum(NODE_ROLE);

/**
 * UI に出すノードの状態（§4.3）。API は `displayStatus` を必ず返し、
 * SPA は `lastSeenAt` から自前で推測しない。導出は `node-status.ts` の
 * `nodeDisplayStatus()` が唯一の正。
 */
export const NODE_DISPLAY_STATUS = [
  'healthy',
  'degraded',
  'unreachable',
  'provisioning',
  'maintenance',
] as const;
export type NodeDisplayStatus = (typeof NODE_DISPLAY_STATUS)[number];
export const nodeDisplayStatusSchema = z.enum(NODE_DISPLAY_STATUS);

/** 証明書の管理主体（§3.4 `certMode`）。 */
export const CERT_MODE = ['control-plane', 'agent'] as const;
export type CertMode = (typeof CERT_MODE)[number];
export const certModeSchema = z.enum(CERT_MODE);

/** 配布中の秘密の世代状態。`retiring` は検証専用（新規署名には使わない）。 */
export const SECRET_STATE = ['active', 'retiring'] as const;
export type SecretState = (typeof SECRET_STATE)[number];
export const secretStateSchema = z.enum(SECRET_STATE);

/** BAN の発生源（ノード DB `banned_ips.source`）。 */
export const BAN_SOURCE = ['agent', 'control-plane', 'manual'] as const;
export type BanSource = (typeof BAN_SOURCE)[number];
export const banSourceSchema = z.enum(BAN_SOURCE);

/**
 * ノードの設定適用状態（`node_desired_state.apply_state`）。
 *
 * `frozen` は「CP 公開鍵で検証できない desired-state を受け取ったので、
 * 適用せず last-good を維持している」状態（R102 / §7.1）。**`failed` と
 * 分ける**理由は運営がやることが真逆だから: `failed` は適用の再試行で直るが、
 * `frozen` は鍵かパイプラインが壊れている（あるいは攻撃されている）ので
 * 再試行してはいけない。同じ箱に入れると「更新が止まっているだけのノード」に
 * 見えて放置される。
 */
export const NODE_APPLY_STATE = [
  'unknown',
  'applying',
  'applied',
  'failed',
  'drifted',
  'frozen',
] as const;
export type NodeApplyState = (typeof NODE_APPLY_STATE)[number];
export const nodeApplyStateSchema = z.enum(NODE_APPLY_STATE);

export const NODE_CREDENTIAL_KIND = [
  'dovecot_master',
  'lmtp_relay',
  'rspamd_controller',
  'agent_bootstrap',
] as const;
export type NodeCredentialKind = (typeof NODE_CREDENTIAL_KIND)[number];
export const nodeCredentialKindSchema = z.enum(NODE_CREDENTIAL_KIND);

/* ------------------------------------------------------ ドメイン・DNS */

export const DOMAIN_STATUS = [
  'pending_verification',
  'verifying',
  'active',
  'dns_error',
  'suspended',
  'deleting',
] as const;
export type DomainStatus = (typeof DOMAIN_STATUS)[number];
export const domainStatusSchema = z.enum(DOMAIN_STATUS);

export const DNS_RECORD_KIND = [
  'ownership_txt',
  'mx',
  'spf',
  'dkim',
  'dmarc',
  'autoconfig',
  'autodiscover',
  'mta_sts',
  'tlsa',
  /**
   * TLS-RPT（RFC 8460）の `_smtp._tls.<domain>` TXT。0035 で DB enum にも追加。
   * MTA-STS を `enforce` へ上げる根拠（失敗レポート）を得るために先に要る。
   */
  'tls_rpt',
] as const;
export type DnsRecordKind = (typeof DNS_RECORD_KIND)[number];
export const dnsRecordKindSchema = z.enum(DNS_RECORD_KIND);

export const DNS_CHECK_RESULT = ['ok', 'missing', 'mismatch', 'error', 'unchecked'] as const;
export type DnsCheckResult = (typeof DNS_CHECK_RESULT)[number];
export const dnsCheckResultSchema = z.enum(DNS_CHECK_RESULT);

export const DMARC_POLICY = ['none', 'quarantine', 'reject'] as const;
export type DmarcPolicy = (typeof DMARC_POLICY)[number];
export const dmarcPolicySchema = z.enum(DMARC_POLICY);

export const SPF_POLICY = ['softfail', 'fail'] as const;
export type SpfPolicy = (typeof SPF_POLICY)[number];
export const spfPolicySchema = z.enum(SPF_POLICY);

export const DKIM_ALGORITHM = ['rsa2048', 'rsa4096', 'ed25519'] as const;
export type DkimAlgorithm = (typeof DKIM_ALGORITHM)[number];
export const dkimAlgorithmSchema = z.enum(DKIM_ALGORITHM);

/* --------------------------------------------------------- メールボックス */

/**
 * コントロールプレーン側のメールボックス状態（§4.6）。
 * ノード側の 3 値とは別物で、写像は `mailbox-state.ts` の
 * `toNodeMailboxState()` が唯一の正。
 */
export const MAILBOX_STATUS = [
  'provisioning',
  'active',
  'over_quota',
  /** 支払い遅延・不正送信による停止。**受信は維持し送信だけ止める** */
  'suspended',
  /** 契約者が明示的に無効化 */
  'disabled',
  'deleting',
  'deleted',
] as const;
export type MailboxStatus = (typeof MAILBOX_STATUS)[number];
export const mailboxStatusSchema = z.enum(MAILBOX_STATUS);

/** ノードローカル DB `mailboxes.status`（§4.2）。CP の 7 値をここへ畳む。 */
export const NODE_MAILBOX_STATUS = ['active', 'readonly', 'suspended', 'deleting'] as const;
export type NodeMailboxStatus = (typeof NODE_MAILBOX_STATUS)[number];
export const nodeMailboxStatusSchema = z.enum(NODE_MAILBOX_STATUS);

/** ノードローカル DB `domains.status`（§4.2）。CP の `DOMAIN_STATUS` とは別語彙。 */
export const NODE_DOMAIN_STATUS = ['active', 'suspended', 'pending', 'deleting'] as const;
export type NodeDomainStatus = (typeof NODE_DOMAIN_STATUS)[number];
export const nodeDomainStatusSchema = z.enum(NODE_DOMAIN_STATUS);

export const SPAM_ACTION = ['tag', 'move_to_junk', 'reject'] as const;
export type SpamAction = (typeof SPAM_ACTION)[number];
export const spamActionSchema = z.enum(SPAM_ACTION);

export const PASSWORD_SCHEME = ['argon2id', 'blf_crypt'] as const;
export type PasswordScheme = (typeof PASSWORD_SCHEME)[number];
export const passwordSchemeSchema = z.enum(PASSWORD_SCHEME);

/* ------------------------------------------------------------ 証明書 */

export const CERTIFICATE_PURPOSE = ['node_mail', 'domain_mail', 'webmail'] as const;
export type CertificatePurpose = (typeof CERTIFICATE_PURPOSE)[number];
export const certificatePurposeSchema = z.enum(CERTIFICATE_PURPOSE);

export const CERTIFICATE_STATUS = [
  'pending',
  'issuing',
  'active',
  'renewing',
  'expired',
  'failed',
  'revoked',
] as const;
export type CertificateStatus = (typeof CERTIFICATE_STATUS)[number];
export const certificateStatusSchema = z.enum(CERTIFICATE_STATUS);

/* --------------------------------------------------- プロビジョニング */

export const JOB_STATUS = [
  'queued',
  'running',
  'waiting',
  'succeeded',
  'failed',
  'canceled',
  'compensating',
  'compensated',
] as const;
export type JobStatus = (typeof JOB_STATUS)[number];
export const jobStatusSchema = z.enum(JOB_STATUS);

export const JOB_KIND = [
  'organization_onboard',
  'domain_onboard',
  'domain_offboard',
  'mailbox_create',
  'mailbox_update',
  'mailbox_delete',
  'node_enroll',
  'node_drain',
  'certificate_issue',
  'certificate_renew',
  'plan_change',
  'webmail_sync',
  'stack_update',
] as const;
export type JobKind = (typeof JOB_KIND)[number];
export const jobKindSchema = z.enum(JOB_KIND);

export const JOB_STEP_STATUS = [
  'pending',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'compensated',
] as const;
export type JobStepStatus = (typeof JOB_STEP_STATUS)[number];
export const jobStepStatusSchema = z.enum(JOB_STEP_STATUS);

/* ------------------------------------------------------ アップデート配信 */

export const ROLLOUT_RING = ['canary', 'ring1', 'ring2'] as const;
export type RolloutRing = (typeof ROLLOUT_RING)[number];
export const rolloutRingSchema = z.enum(ROLLOUT_RING);

export const ROLLOUT_STATUS = [
  'draft',
  'running',
  'paused',
  'halted',
  'completed',
  'rolled_back',
] as const;
export type RolloutStatus = (typeof ROLLOUT_STATUS)[number];
export const rolloutStatusSchema = z.enum(ROLLOUT_STATUS);

export const ROLLOUT_TARGET_STATUS = [
  'pending',
  'updating',
  'verifying',
  'succeeded',
  'failed',
  'rolled_back',
  'skipped',
] as const;
export type RolloutTargetStatus = (typeof ROLLOUT_TARGET_STATUS)[number];
export const rolloutTargetStatusSchema = z.enum(ROLLOUT_TARGET_STATUS);

/* ------------------------------------------------------------ セキュリティ */

export const IP_RULE_ACTION = ['block', 'allow'] as const;
export type IpRuleAction = (typeof IP_RULE_ACTION)[number];
export const ipRuleActionSchema = z.enum(IP_RULE_ACTION);

export const IP_RULE_SOURCE = ['manual', 'auth_bruteforce', 'spam_abuse', 'rate_limit'] as const;
export type IpRuleSource = (typeof IP_RULE_SOURCE)[number];
export const ipRuleSourceSchema = z.enum(IP_RULE_SOURCE);

/* -------------------------------------------------------- Webmail 連携 */

export const WEBMAIL_SYNC_STATE = ['pending', 'synced', 'drifted', 'failed', 'disabled'] as const;
export type WebmailSyncState = (typeof WEBMAIL_SYNC_STATE)[number];
export const webmailSyncStateSchema = z.enum(WEBMAIL_SYNC_STATE);

/* --------------------------------------------- 配信コンテンツ CMS（CD §5.2） */

/** `content_documents.kind`。`snippet` は他文書から参照される部品。 */
export const CONTENT_KIND = ['campaign', 'template', 'snippet', 'layout'] as const;
export type ContentKind = (typeof CONTENT_KIND)[number];
export const contentKindSchema = z.enum(CONTENT_KIND);

/**
 * `content_revisions.doc_format`。
 * 値の実体（ブロック JSON のスキーマ）は `content.ts` が唯一の正。
 */
export const CONTENT_DOC_FORMAT = ['blocks-v1', 'raw-html-v1'] as const;
export type ContentDocFormatValue = (typeof CONTENT_DOC_FORMAT)[number];
export const contentDocFormatSchema = z.enum(CONTENT_DOC_FORMAT);

/** `content_revisions.review_state`。遷移表は `content-state.ts` が唯一の正。 */
export const CONTENT_REVIEW_STATE = ['draft', 'in_review', 'approved', 'changes_requested'] as const;
export type ContentReviewState = (typeof CONTENT_REVIEW_STATE)[number];
export const contentReviewStateSchema = z.enum(CONTENT_REVIEW_STATE);

/**
 * `content_revisions.upload_state`。本文の実体が S3 に確定したかを表す。
 * `review_state` とは**直交**する（承認の話ではなく、実体の有無の話）。
 */
export const CONTENT_UPLOAD_STATE = ['pending_upload', 'committed', 'failed', 'abandoned'] as const;
export type ContentUploadState = (typeof CONTENT_UPLOAD_STATE)[number];
export const contentUploadStateSchema = z.enum(CONTENT_UPLOAD_STATE);

/**
 * `content_revisions.retention_class`。
 * `sent_evidence` は送信済みメールの証跡なので刈り取り対象から外れる。
 */
export const CONTENT_RETENTION_CLASS = ['transient', 'sent_evidence'] as const;
export type ContentRetentionClass = (typeof CONTENT_RETENTION_CLASS)[number];
export const contentRetentionClassSchema = z.enum(CONTENT_RETENTION_CLASS);

/** `content_documents.origin`。障害調査では「誰が」より先に「どこから来たか」が要る。 */
export const CONTENT_ORIGIN = ['portal', 'api', 'import', 'template'] as const;
export type ContentOrigin = (typeof CONTENT_ORIGIN)[number];
export const contentOriginSchema = z.enum(CONTENT_ORIGIN);

export const CONTENT_TAG_KIND = ['document', 'asset', 'both'] as const;
export type ContentTagKind = (typeof CONTENT_TAG_KIND)[number];
export const contentTagKindSchema = z.enum(CONTENT_TAG_KIND);

export const CONTENT_VARIANT_KIND = ['resize', 'format', 'thumbnail'] as const;
export type ContentVariantKind = (typeof CONTENT_VARIANT_KIND)[number];
export const contentVariantKindSchema = z.enum(CONTENT_VARIANT_KIND);

/** `content_schedules.action`。`approve` は統合面から作れない（§4.6）。 */
export const CONTENT_SCHEDULE_ACTION = ['approve', 'archive', 'unarchive'] as const;
export type ContentScheduleAction = (typeof CONTENT_SCHEDULE_ACTION)[number];
export const contentScheduleActionSchema = z.enum(CONTENT_SCHEDULE_ACTION);

export const CONTENT_SCHEDULE_STATE = ['pending', 'running', 'done', 'failed', 'cancelled'] as const;
export type ContentScheduleState = (typeof CONTENT_SCHEDULE_STATE)[number];
export const contentScheduleStateSchema = z.enum(CONTENT_SCHEDULE_STATE);

export const CONTENT_LINK_RESULT = [
  'ok',
  'redirect',
  'client_error',
  'server_error',
  'dns_error',
  'timeout',
  /** SSRF ガード（`outbound-url`）が拒否したレンジ。 */
  'blocked',
] as const;
export type ContentLinkResult = (typeof CONTENT_LINK_RESULT)[number];
export const contentLinkResultSchema = z.enum(CONTENT_LINK_RESULT);

/**
 * `content_reviews.decision`。`content_revisions.review_state` とは別語彙。
 * 状態（今どうなっているか）と決裁（誰が何を決めたか）を同じ enum にすると、
 * 「差し戻し後にまた差し戻した」の 2 件目が状態の重複として潰れる。
 */
export const CONTENT_REVIEW_DECISION = ['approved', 'changes_requested', 'withdrawn'] as const;
export type ContentReviewDecision = (typeof CONTENT_REVIEW_DECISION)[number];
export const contentReviewDecisionSchema = z.enum(CONTENT_REVIEW_DECISION);

/** `content_templates.scope`。`platform` は運営者が公開する共有テンプレート。 */
export const CONTENT_TEMPLATE_SCOPE = ['org', 'platform'] as const;
export type ContentTemplateScope = (typeof CONTENT_TEMPLATE_SCOPE)[number];
export const contentTemplateScopeSchema = z.enum(CONTENT_TEMPLATE_SCOPE);

/** `content_assets.status`。`quarantined` はウイルス検知・権利侵害申告での隔離。 */
export const CONTENT_ASSET_STATUS = ['uploading', 'active', 'quarantined', 'deleted'] as const;
export type ContentAssetStatus = (typeof CONTENT_ASSET_STATUS)[number];
export const contentAssetStatusSchema = z.enum(CONTENT_ASSET_STATUS);

/** `content_imports.source_kind`。**`contact_imports`（宛先の取り込み）とは別物**。 */
export const CONTENT_IMPORT_SOURCE = ['json_bundle', 'html_file', 'aurora_template'] as const;
export type ContentImportSource = (typeof CONTENT_IMPORT_SOURCE)[number];
export const contentImportSourceSchema = z.enum(CONTENT_IMPORT_SOURCE);

export const CONTENT_IMPORT_STATUS = [
  'uploaded',
  'validating',
  'preview_ready',
  'applying',
  'completed',
  'failed',
] as const;
export type ContentImportStatus = (typeof CONTENT_IMPORT_STATUS)[number];
export const contentImportStatusSchema = z.enum(CONTENT_IMPORT_STATUS);

/* ------------------------------------------------------------ キャンペーン */

/**
 * `campaigns.status`。遷移表は `content-state.ts` の `CAMPAIGN_TRANSITIONS` が唯一の正。
 *
 * `awaiting_release` は「API がスケジュールを入れたが、人がまだ解禁していない」踊り場。
 * これがあるおかげで、統合面に `start` を出さずにスケジュール投入まで自動化できる。
 *
 * **綴りは `canceled`（l 1 個）**。既存の `SUBSCRIPTION_STATUS` と揃える。
 * 設計書（CMS_GAP_AND_API.md §4.6）の `cancelled` は表記ゆれなので実装側に寄せた。
 */
export const CAMPAIGN_STATUS = [
  'draft',
  'validated',
  'test_sent',
  'awaiting_release',
  'scheduled',
  'sending',
  'paused',
  'halted',
  'completed',
  'canceled',
  'failed',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUS)[number];
export const campaignStatusSchema = z.enum(CAMPAIGN_STATUS);

/**
 * **Postgres の `campaign_status` 型に実在する値**（0005 で作成、0007 で `halted` を追加）。
 *
 * 上の `CAMPAIGN_STATUS`（設計・API・SPA の語彙）と 1 語だけずれている。
 * DB は `cancelled`（l 2 つ・設計書の綴り）、shared は `canceled`（l 1 つ・
 * 既存 `SUBSCRIPTION_STATUS` に合わせた綴り）で、0005 の適用後に発覚した。
 *
 * **綴りを揃えるための `ALTER TYPE ... RENAME VALUE` は行わない。**
 * 既に `cancelled` を書き込む実装（apps/api の `toDbCampaignStatus()`）が動いており、
 * 型の rename は「migrate を流した瞬間に API が落ちる」変更になる。
 * 代わりに **両方の語彙を shared から出す**ことで、写像の定義が
 * apps/api の中だけにある状態（= grep しないと存在に気付けない状態）を解消する。
 * 写像は `campaignStatusToDb()` / `campaignStatusFromDb()` が唯一の正。
 */
export const CAMPAIGN_STATUS_DB = [
  'draft',
  'validated',
  'test_sent',
  'awaiting_release',
  'scheduled',
  'sending',
  'paused',
  'completed',
  'cancelled',
  'failed',
  /** 0007 で追加。これが無いと「止めたつもりで止まっていない」が作れてしまう。 */
  'halted',
] as const;
export type CampaignStatusDb = (typeof CAMPAIGN_STATUS_DB)[number];

/** 状態機械（shared）の語彙 → DB の値。 */
export function campaignStatusToDb(status: CampaignStatus): CampaignStatusDb {
  return (status === 'canceled' ? 'cancelled' : status) as CampaignStatusDb;
}

/** DB の値 → 状態機械（shared）の語彙。 */
export function campaignStatusFromDb(status: CampaignStatusDb | string): CampaignStatus {
  return (status === 'cancelled' ? 'canceled' : status) as CampaignStatus;
}

/**
 * **Postgres の `content_upload_state` 型に実在する値**（0005 で作成）。
 *
 * 上の `CONTENT_UPLOAD_STATE`（設計語彙）は `committed` / `abandoned` を持つが、
 * DB 側は `stored` の 1 語で「実体が確定した」を表している。
 * これも 0005 適用後に発覚した食い違いで、**rename も追加もしない**。
 * `abandoned` に相当する行は `upload_state='pending_upload'` かつ
 * `upload_expires_at < now()` で表現でき、DB に新しい値を足さなくても
 * sweep の判定は書ける（値を足すと既存の部分インデックスの述語も直す必要が出る）。
 */
export const CONTENT_UPLOAD_STATE_DB = ['pending_upload', 'stored', 'failed'] as const;
export type ContentUploadStateDb = (typeof CONTENT_UPLOAD_STATE_DB)[number];

/* ------------------------------------------- 出口 IP と送信ストリーム（PD §5.1） */

/**
 * `outbound_pools.kind`。
 * `quarantine` は「評判が落ちた IP を隔離して、それでも止められない
 * トランザクションメールだけを流す」逃がし先。
 *
 * `mixed`（併用出口・0078）は「この 1 本の IP でトランザクションと
 * 一斉配信の**両方**を運ぶ」と運営が明示宣言したプール。公開 IPv4 が
 * 1 本しか無い構成（スモールスタート / 1 台での検証）のためにある。
 *
 * **これは代替ではなく宣言。** `selectOutboundPool()` は今も kind の
 * 代替を一切しない（`transactional` のプールが `bulk` に選ばれることは
 * 無い）。`mixed` は両方を運ぶ別の種別で、しかも完全一致するプールが
 * 候補に 1 つでもあれば必ず負けるよう並べてある。
 *
 * **`shared` と名付けなかった理由**: 0076 以来「共有 =
 * `dedicated_organization_id IS NULL`」の意味で "共有" を使っている。
 * kind に `shared` を足すと二重の意味になり、運営画面で
 * 「専有の共有出口」が文法上作れてしまう。
 */
export const OUTBOUND_POOL_KIND = ['transactional', 'bulk', 'quarantine', 'mixed'] as const;
export type OutboundPoolKind = (typeof OUTBOUND_POOL_KIND)[number];
export const outboundPoolKindSchema = z.enum(OUTBOUND_POOL_KIND);

/**
 * `outbound_kind_daily.purpose`（0079）。併用出口で「一斉配信だけ止める」
 * 判断のための用途別の分母と分子。
 *
 * `unknown` は**苦情の帰属が引けなかった分**。Original-Mail-From を伏せる
 * ISP があり、VERP が引けないと用途が確定しない。この分は判定で
 * **一斉配信側へ寄せる**（安全側。詳細は `evaluateMixedEgressGuard`）。
 */
export const OUTBOUND_TRAFFIC_PURPOSE = ['transactional', 'bulk', 'unknown'] as const;
export type OutboundTrafficPurpose = (typeof OUTBOUND_TRAFFIC_PURPOSE)[number];
export const outboundTrafficPurposeSchema = z.enum(OUTBOUND_TRAFFIC_PURPOSE);

/** `outbound_pools.tier`。既存 `POOL_TIER`（`node_pools`）とは別概念なので流用しない。 */
export const OUTBOUND_POOL_TIER = ['warmup', 'standard', 'penalty'] as const;
export type OutboundPoolTier = (typeof OUTBOUND_POOL_TIER)[number];
export const outboundPoolTierSchema = z.enum(OUTBOUND_POOL_TIER);

export const OUTBOUND_POOL_STATUS = ['active', 'draining', 'quarantined', 'retired'] as const;
export type OutboundPoolStatus = (typeof OUTBOUND_POOL_STATUS)[number];
export const outboundPoolStatusSchema = z.enum(OUTBOUND_POOL_STATUS);

/**
 * `outbound_pool_ips.status`。
 * **`fbl_registered_at IS NULL` の IP は `active` にしない**（PD §5.1）。
 * FBL（苦情フィードバック）が届かない IP で大量配信すると、
 * 苦情が観測できないまま評判だけが落ちる。
 */
export const OUTBOUND_IP_STATUS = [
  'pending',
  'warming',
  'active',
  'quarantined',
  'retired',
] as const;
export type OutboundIpStatus = (typeof OUTBOUND_IP_STATUS)[number];
export const outboundIpStatusSchema = z.enum(OUTBOUND_IP_STATUS);

/** `outbound_pool_ips.rbl_status`。`deliverability` キューの `rbl.check` が更新する。 */
export const RBL_STATUS = ['unknown', 'clean', 'listed', 'delisting'] as const;
export type RblStatus = (typeof RBL_STATUS)[number];
export const rblStatusSchema = z.enum(RBL_STATUS);

/**
 * `custom_hostnames.kind`（0017）。
 *
 * **`mail` はここに入れない。** `mail.<domain>` は `domains.mail_hostname` が
 * 既に担っていて、同じ意味の列を 2 か所に持つと必ず食い違う。
 */
export const CUSTOM_HOSTNAME_KIND = ['webmail', 'link'] as const;
export type CustomHostnameKind = (typeof CUSTOM_HOSTNAME_KIND)[number];
export const customHostnameKindSchema = z.enum(CUSTOM_HOSTNAME_KIND);

/**
 * `custom_hostnames.status`。
 * **`active` にできるのは検証ジョブだけ。** 契約者の自己申告で active にすると、
 * 証明書の無いホスト名が配られて TLS が失敗する。
 */
export const CUSTOM_HOSTNAME_STATUS = ['pending', 'verifying', 'active', 'failed'] as const;
export type CustomHostnameStatus = (typeof CUSTOM_HOSTNAME_STATUS)[number];
export const customHostnameStatusSchema = z.enum(CUSTOM_HOSTNAME_STATUS);

/** `sending_streams.kind`。トランザクションと一斉配信で出口 IP を必ず分ける。 */
export const SENDING_STREAM_KIND = ['transactional', 'bulk'] as const;
export type SendingStreamKind = (typeof SENDING_STREAM_KIND)[number];
export const sendingStreamKindSchema = z.enum(SENDING_STREAM_KIND);

/**
 * `sending_streams.status`。
 * `verified` にできるのは `domains.dns_status='ok'` かつ bounce エイリアスの
 * 疎通が取れたときだけ（PD §5.1）。判定は `dns-verify` キューが行う。
 */
export const SENDING_STREAM_STATUS = [
  'draft',
  'verifying',
  'verified',
  'throttled',
  'suspended',
] as const;
export type SendingStreamStatus = (typeof SENDING_STREAM_STATUS)[number];
export const sendingStreamStatusSchema = z.enum(SENDING_STREAM_STATUS);

/** `outbound_assignments.reason`。履歴を残すので「なぜその割り当てにしたか」が要る。 */
export const OUTBOUND_ASSIGNMENT_REASON = [
  'default',
  'manual',
  'warmup',
  'penalty',
  'migration',
] as const;
export type OutboundAssignmentReason = (typeof OUTBOUND_ASSIGNMENT_REASON)[number];
export const outboundAssignmentReasonSchema = z.enum(OUTBOUND_ASSIGNMENT_REASON);

/* --------------------------------------------------- 宛先の取り込みと配信実行 */

/**
 * `contact_imports.status`。値は `CONTENT_IMPORT_STATUS` と同じだが**別の型**にする。
 * 文書の取り込み（content）と宛先の取り込み（contact）は 1 文字違いで
 * 権限も対象も別なので、同じ型を共有すると取り違えたときに型検査が助けてくれない。
 */
export const CONTACT_IMPORT_STATUS = [
  'uploaded',
  'validating',
  'preview_ready',
  'applying',
  'completed',
  'failed',
] as const;
export type ContactImportStatus = (typeof CONTACT_IMPORT_STATUS)[number];
export const contactImportStatusSchema = z.enum(CONTACT_IMPORT_STATUS);

/** `campaign_recipients.state`。1 宛先の一生をここで表す。 */
export const CAMPAIGN_RECIPIENT_STATE = [
  'pending',
  /** ノードがバッチをリースした。二重注入を防ぐための中間状態。 */
  'leased',
  'injected',
  'delivered',
  'soft_bounced',
  'hard_bounced',
  'complained',
  'unsubscribed',
  /** 展開時にサプレッションに当たったので**送らなかった**。 */
  'suppressed',
  'failed',
] as const;
export type CampaignRecipientState = (typeof CAMPAIGN_RECIPIENT_STATE)[number];
export const campaignRecipientStateSchema = z.enum(CAMPAIGN_RECIPIENT_STATE);

export const CAMPAIGN_BATCH_STATE = [
  'pending',
  'leased',
  'injected',
  'failed',
  'canceled',
] as const;
export type CampaignBatchState = (typeof CAMPAIGN_BATCH_STATE)[number];
export const campaignBatchStateSchema = z.enum(CAMPAIGN_BATCH_STATE);

/**
 * `campaign_events.kind`。
 *
 * **`opened` は無い。** PD §5.1 の表には載っているが、開封の観測手段は
 * 開封ピクセルしかなく、それは CMS_DESIGN §7「やらないこと」で明示的に禁じている。
 * 値だけ用意しておくと「発生源が無いのに 0 件が表示される」指標が UI に生まれ、
 * いずれ誰かがピクセルを実装する動機になる。**語彙の側で塞ぐ。**
 */
export const CAMPAIGN_EVENT_KIND = [
  'injected',
  'delivered',
  'deferred',
  'bounced',
  'complained',
  'unsubscribed',
  'replied',
  /** v2 のクリック計測。リダイレクト先は `campaign_links` を引く（トークンに埋めない）。 */
  'clicked',
] as const;
export type CampaignEventKind = (typeof CAMPAIGN_EVENT_KIND)[number];
export const campaignEventKindSchema = z.enum(CAMPAIGN_EVENT_KIND);

/**
 * `unsubscribe_records.method`。
 * `oneclick_http` / `oneclick_mailto` は RFC 8058 の One-Click 解除。
 */
export const UNSUBSCRIBE_METHOD = [
  'oneclick_http',
  'oneclick_mailto',
  'preference_center',
  'manual',
  'complaint',
] as const;
export type UnsubscribeMethod = (typeof UNSUBSCRIBE_METHOD)[number];
export const unsubscribeMethodSchema = z.enum(UNSUBSCRIBE_METHOD);

/* ------------------------------------------------- 公開フォーム（CD §5.2） */

export const SIGNUP_FORM_STATUS = ['draft', 'published', 'paused', 'archived'] as const;
export type SignupFormStatus = (typeof SIGNUP_FORM_STATUS)[number];
export const signupFormStatusSchema = z.enum(SIGNUP_FORM_STATUS);

/**
 * `signup_forms.challenge_mode`。
 * 公開フォームの脅威は CSRF ではなく **list-bombing**（D31）。
 * `auto` は流入が閾値を超えたときだけチャレンジを出す。
 */
export const SIGNUP_CHALLENGE_MODE = ['off', 'auto', 'always'] as const;
export type SignupChallengeMode = (typeof SIGNUP_CHALLENGE_MODE)[number];
export const signupChallengeModeSchema = z.enum(SIGNUP_CHALLENGE_MODE);

/** `signup_submissions.state`。未確認の生メールは 72 時間で NULL 化する（D23）。 */
export const SIGNUP_SUBMISSION_STATE = [
  'pending_confirm',
  'confirmed',
  'expired',
  'rejected',
] as const;
export type SignupSubmissionState = (typeof SIGNUP_SUBMISSION_STATE)[number];
export const signupSubmissionStateSchema = z.enum(SIGNUP_SUBMISSION_STATE);

/**
 * `break_glass_grants.target_type`（C17）。
 * 「読めない」を貫くと正式な要請に応じられず、非公式な手段（S3 コンソール直参照）が
 * 生まれて監査が消える。**経路を 1 本だけ用意して全部記録する**ための語彙。
 */
export const BREAK_GLASS_TARGET_TYPE = [
  'content_revision',
  'content_artifact',
  'campaign',
] as const;
export type BreakGlassTargetType = (typeof BREAK_GLASS_TARGET_TYPE)[number];
export const breakGlassTargetTypeSchema = z.enum(BREAK_GLASS_TARGET_TYPE);

/* ---------------------------------------------------------------- Webhook */

/**
 * `webhook_endpoints.status`。
 * `disabled_by_failure` からの復帰は portal の enable のみ（自動復帰はしない）。
 */
export const WEBHOOK_ENDPOINT_STATUS = ['active', 'paused', 'disabled_by_failure', 'revoked'] as const;
export type WebhookEndpointStatus = (typeof WEBHOOK_ENDPOINT_STATUS)[number];
export const webhookEndpointStatusSchema = z.enum(WEBHOOK_ENDPOINT_STATUS);

/**
 * `webhook_deliveries.state`。
 * `skipped` は「無効化中に発生したので配送しなかった」行。
 * **行だけは作る**（何が飛ばなかったかを契約者が見られないと、復旧の判断ができない）。
 */
export const WEBHOOK_DELIVERY_STATE = [
  'pending',
  'in_flight',
  'succeeded',
  'failed',
  'exhausted',
  'skipped',
] as const;
export type WebhookDeliveryState = (typeof WEBHOOK_DELIVERY_STATE)[number];
export const webhookDeliveryStateSchema = z.enum(WEBHOOK_DELIVERY_STATE);

/* --------------------------------------------- 宛先台帳とサプレッション */

/**
 * `contacts.status`（組織全体での状態）。
 * 値は `packages/db/src/schema/_enums.ts` の `CONTACT_STATUS` と一致させること。
 */
export const CONTACT_STATUS = [
  'subscribed',
  'unsubscribed',
  'bounced',
  'complained',
  'pending_optin',
] as const;
export type ContactStatus = (typeof CONTACT_STATUS)[number];
export const contactStatusSchema = z.enum(CONTACT_STATUS);

/**
 * `contact_list_memberships.status`（リスト単位の状態）。
 * `contacts.status` と**別語彙**なのは、1 つのリストだけ解除して他は購読継続、
 * という状態を表現できなくなるため。
 */
export const CONTACT_MEMBERSHIP_STATUS = ['pending_optin', 'subscribed', 'unsubscribed', 'cleaned'] as const;
export type ContactMembershipStatus = (typeof CONTACT_MEMBERSHIP_STATUS)[number];
export const contactMembershipStatusSchema = z.enum(CONTACT_MEMBERSHIP_STATUS);

export const SUPPRESSION_SCOPE = ['campaign', 'organization'] as const;
export type SuppressionScope = (typeof SUPPRESSION_SCOPE)[number];
export const suppressionScopeSchema = z.enum(SUPPRESSION_SCOPE);

/**
 * `suppressions.reason`。
 * **`unsubscribe` の行は契約者が解除できない**（アプリ層で 403）。
 * 画面はこの値を見て解除ボタンの有無を決める。
 */
export const SUPPRESSION_REASON = [
  'hard_bounce',
  /**
   * 一時失敗（4.x.x / 満杯）が**連続して**規定回数に達したときの期限付き抑止。
   * R0 §2.3 で追加。`expires_at` が必ず入る点が他の理由と違う。
   * 1 回の 4xx で恒久抑止すると、相手の一時障害で宛先リストが削れていく。
   */
  'soft_bounce',
  'complaint',
  'unsubscribe',
  'manual',
  'spamtrap',
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASON)[number];
export const suppressionReasonSchema = z.enum(SUPPRESSION_REASON);

export const SUPPRESSION_RELEASE_REASON = ['resubscribe', 'manual_ops'] as const;
export type SuppressionReleaseReason = (typeof SUPPRESSION_RELEASE_REASON)[number];
export const suppressionReleaseReasonSchema = z.enum(SUPPRESSION_RELEASE_REASON);

/**
 * 同意の取得方法（`optin_records.method` 相当）。
 *
 * `imported_with_evidence` を残してあるのは、既存の名簿を移行する現実的な経路が
 * 無いと「証跡なしで API を叩く」抜け道が作られるため。
 */
export const CONTACT_OPTIN_METHOD = [
  'double_optin',
  'single_optin',
  'offline',
  'imported_with_evidence',
] as const;
export type ContactOptinMethod = (typeof CONTACT_OPTIN_METHOD)[number];
export const contactOptinMethodSchema = z.enum(CONTACT_OPTIN_METHOD);

/* ------------------------------------------------- バッジ表示のトーン対応 */

export type Tone = 'success' | 'warning' | 'destructive' | 'info' | 'muted' | 'default';

/**
 * 状態値 → バッジのトーン。
 * SPA 側でここを参照すれば admin / portal で色が食い違わない。
 */
export const STATUS_TONE: Record<string, Tone> = {
  // 契約
  pending: 'muted',
  active: 'success',
  suspended: 'destructive',
  closing: 'warning',
  closed: 'muted',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'muted',
  // 請求
  draft: 'muted',
  open: 'info',
  paid: 'success',
  void: 'muted',
  uncollectible: 'destructive',
  // ノード
  provisioning: 'info',
  enrolling: 'info',
  draining: 'warning',
  maintenance: 'warning',
  decommissioned: 'muted',
  healthy: 'success',
  degraded: 'warning',
  critical: 'destructive',
  unreachable: 'destructive',
  unknown: 'muted',
  applying: 'info',
  applied: 'success',
  drifted: 'warning',
  // ドメイン
  pending_verification: 'warning',
  verifying: 'info',
  dns_error: 'destructive',
  deleting: 'muted',
  ok: 'success',
  missing: 'destructive',
  mismatch: 'warning',
  error: 'destructive',
  unchecked: 'muted',
  // メールボックス
  over_quota: 'warning',
  readonly: 'warning',
  deleted: 'muted',
  // 秘密の世代
  retiring: 'warning',
  // 証明書
  issuing: 'info',
  renewing: 'info',
  expired: 'destructive',
  revoked: 'muted',
  // ジョブ
  queued: 'muted',
  running: 'info',
  waiting: 'warning',
  succeeded: 'success',
  failed: 'destructive',
  compensating: 'warning',
  compensated: 'muted',
  skipped: 'muted',
  // 配信
  paused: 'warning',
  halted: 'destructive',
  completed: 'success',
  rolled_back: 'warning',
  updating: 'info',
  // Webmail
  synced: 'success',
  disabled: 'muted',
  // コンテンツのレビュー
  in_review: 'info',
  approved: 'success',
  changes_requested: 'warning',
  // 本文アップロード
  pending_upload: 'info',
  committed: 'success',
  abandoned: 'muted',
  transient: 'muted',
  sent_evidence: 'info',
  // キャンペーン
  validated: 'info',
  test_sent: 'info',
  awaiting_release: 'warning',
  scheduled: 'info',
  sending: 'info',
  // 予約実行
  done: 'success',
  cancelled: 'muted',
  // リンク検査
  redirect: 'warning',
  client_error: 'destructive',
  server_error: 'destructive',
  timeout: 'warning',
  blocked: 'destructive',
  // 取り込み
  uploaded: 'info',
  validating: 'info',
  preview_ready: 'info',
  // Webhook
  disabled_by_failure: 'destructive',
  in_flight: 'info',
  exhausted: 'destructive',
  // 宛先とサプレッション
  subscribed: 'success',
  unsubscribed: 'muted',
  pending_optin: 'warning',
  cleaned: 'muted',
  bounced: 'destructive',
  complained: 'destructive',
  hard_bounce: 'destructive',
  complaint: 'destructive',
  // `unsubscribe` は受信者本人の意思。異常ではないので警告色にしない。
  unsubscribe: 'muted',
  spamtrap: 'destructive',
  manual: 'info',
  // 出口 IP と送信ストリーム
  transactional: 'info',
  bulk: 'info',
  warmup: 'warning',
  standard: 'default',
  penalty: 'destructive',
  quarantined: 'destructive',
  retired: 'muted',
  warming: 'warning',
  clean: 'success',
  listed: 'destructive',
  delisting: 'warning',
  verified: 'success',
  throttled: 'warning',
  // 配信実行
  leased: 'info',
  injected: 'info',
  delivered: 'success',
  soft_bounced: 'warning',
  hard_bounced: 'destructive',
  suppressed: 'muted',
  deferred: 'warning',
  replied: 'success',
  clicked: 'info',
  // 公開フォーム
  published: 'success',
  archived: 'muted',
  pending_confirm: 'warning',
  confirmed: 'success',
  rejected: 'destructive',
  off: 'muted',
  auto: 'info',
  always: 'warning',
  /*
   * プライベートメールアドレス（`PRIVATE_ADDRESS_STATUS`）。
   * `active` / `disabled` は上で定義済みなので `burned` だけを足す。
   *
   * **`disabled`（muted）と同じ色にしない。** 配送上の挙動は同じでも、
   * `burned` は「渡した相手が名簿を漏らした」の記録であって、
   * 一覧を眺めたときに真っ先に目に入る必要がある（どのサービスが
   * 漏らしたかを追うのがこの機能の目的そのもの）。
   */
  burned: 'destructive',
};

export function toneOf(status: string | null | undefined): Tone {
  if (!status) return 'muted';
  return STATUS_TONE[status] ?? 'default';
}

/* ------------------------------------------------- 一覧の絞り込み（検索条件） */

/**
 * ドメイン一覧の「証明書」フィルタ（A316）。
 *
 * **API と管理コンソールで同じ値を使うためにここへ置く。** 以前は画面側だけが
 * `expired` / `missing` を持ち、API 側の zod は `expiring | none | active` しか
 * 受けなかったので、その 2 つを選ぶと 400 になって一覧が空になっていた。
 * 値の集合を 1 か所に置けば、片側だけ増やしても型で気付ける。
 *
 * - `active`   … 最新の証明書が有効
 * - `expiring` … 30 日以内に切れる（まだ切れていない）
 * - `expired`  … 期限切れ
 * - `none`     … 証明書が 1 枚も無い
 */
export const CERT_FILTER = ['active', 'expiring', 'expired', 'none'] as const;
export type CertFilter = (typeof CERT_FILTER)[number];
export const certFilterSchema = z.enum(CERT_FILTER);
